import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { socketService } from '@/services/socket';
import Cookies from 'js-cookie';

/**
 * Site Config Hook
 * Manages global site configuration and user-specific configuration
 * Based on existing implementation from ibitplay
 */

// Initial state object (from old codebase)
const initialState = {
  registerbonus: 0,
  affiliatebonus: 0,
  comissionpercent: 0,
  sports: false,
  inr: false,
  mvr: false,
  aed: false,
  pkr: false,
  cryptocoin: false,
  bjt: false,
  casino: false,
  lotto: false,
  vipclub: false,
  clubmembership: false,
  bonus: false,
  affiliate: false,
  crash: false,
  originals: false,
  livegames: false,
  slotsgames: false,
  alllivegames: false,
  allslotsgames: false,
  lotterygames: false,
  indiangames: false,
  cards: false,
  jilli: false,
  bdt: false,
  clubrake: 0,
};

// Create context
const SiteConfigContext = createContext({
  siteConfig: initialState,
  userConfig: null,
  userInfo: null,
  updateSiteConfig: () => {},
  updateUserConfig: () => {},
  isLoading: true,
});

/**
 * Site Config Provider Component
 */
export function SiteConfigProvider({ children }) {
  const [siteConfig, setSiteConfig] = useState(initialState);
  const [userConfig, setUserConfig] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Handle global site config updates
  const handleSiteConfigUpdate = useCallback((data) => {
    console.log('🌍 Site config updated:', data);
    if (data) {
      setSiteConfig((prev) => ({ ...prev, ...data }));
    }
    setIsLoading(false);
  }, []);

  // Handle connection errors gracefully
  const handleConnectionError = useCallback((error) => {
    console.warn('⚠️ Socket connection error, continuing without socket:', error);
    // Set loading to false even if socket fails, so app doesn't hang
    setIsLoading(false);
  }, []);

  // Handle user-specific config updates
  const handleUserConfigUpdate = useCallback((data) => {
    console.log('👤 User config updated:', data);
    if (data) {
      setUserConfig(data);
    }
  }, []);

  // Handle user info updates
  const handleUserInfo = useCallback((data) => {
    console.log('👤 User info received:', data);
    if (data) {
      setUserInfo(data);
    }
  }, []);

  useEffect(() => {
    // Connect socket (will connect even without token for site config)
    socketService.connect();

    // Wait for connection before setting up listeners
    const setupListeners = () => {
    // Setup site config listeners
    socketService.on('siteConfigUpdated', handleSiteConfigUpdate);
    socketService.on('userConfigUpdated', handleUserConfigUpdate);
    socketService.on('user_info', handleUserInfo);
    socketService.on('connect_error', handleConnectionError);

      // Request initial configs (if socket is connected)
      if (socketService.getIsConnected()) {
        socketService.emitToServer('requestSiteConfig');
        socketService.emitToServer('requestUserConfig');
      }

      // Identify user if logged in (for user-specific config)
      const uid = Cookies.get('uid') || localStorage.getItem('uid');
      if (uid) {
        const userId = Number(uid);
        if (!isNaN(userId) && socketService.getIsConnected()) {
          socketService.emitToServer('identify', userId);
        }
      }
    };

    // Setup listeners immediately
    setupListeners();

    // Also setup when socket connects
    socketService.on('connect', () => {
      setupListeners();
    });

    // Cleanup
    return () => {
      socketService.off('siteConfigUpdated', handleSiteConfigUpdate);
      socketService.off('userConfigUpdated', handleUserConfigUpdate);
      socketService.off('user_info', handleUserInfo);
      socketService.off('connect', setupListeners);
      socketService.off('connect_error', handleConnectionError);
    };
  }, [handleSiteConfigUpdate, handleUserConfigUpdate, handleUserInfo, handleConnectionError]);

  const contextValue = {
    siteConfig,
    userConfig,
    userInfo,
    isLoading,
    updateSiteConfig: (newConfig) => setSiteConfig((prev) => ({ ...prev, ...newConfig })),
    updateUserConfig: (newConfig) => setUserConfig((prev) => ({ ...prev, ...newConfig })),
  };

  return (
    <SiteConfigContext.Provider value={contextValue}>
      {children}
    </SiteConfigContext.Provider>
  );
}

/**
 * Hook to use site config
 */
export function useSiteConfig() {
  const context = useContext(SiteConfigContext);
  if (!context) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
}

export default useSiteConfig;
